// ============================================================================
// "My Work" unified inbox data layer.
//
// One read path: SECURITY DEFINER RPC get_my_work() aggregates everything
// pending for the current user across 13 sources (routing, rate proposals,
// user approvals, supply, sub replacement, contracts, hiring, AM Collect,
// incentives, deletion requests, facility access proposals, my tasks, app
// feedback). Each branch in the RPC encodes the SAME authorization gate the
// source module enforces — see migration unified_inbox_get_my_work_rpc.
//
// Inline writes deliberately REUSE the source modules' exact write paths
// (same RPCs / same UPDATE payloads) — no forked approval logic here.
// ============================================================================
import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';
import { errorMessage } from '../../../shared/errors';

export type MyWorkActionKind =
  | 'inline_ack'
  | 'inline_approve'
  | 'inline_approve_user'
  | 'inline_complete'
  | 'inline_delete'
  | 'link_only';

export interface MyWorkItem {
  item_type: string;
  item_id: string;
  entity_type: string;
  entity_id: string;
  title: string;
  summary: string | null;
  requested_by: string | null;
  created_at: string;
  age_days: number;
  urgency: string | null;
  waiting_on_me: boolean;
  action_kind: MyWorkActionKind;
  deep_link_path: string | null;
  source_status: string;
}

// Bucket = the "Type" grouping in the UI. Keep labels human, not table-y.
export type MyWorkBucket = 'approvals' | 'routed' | 'tasks' | 'feedback' | 'updates';

export const BUCKET_LABEL: Record<MyWorkBucket, string> = {
  approvals: 'Approvals',
  routed: 'Routed to me',
  tasks: 'My tasks',
  feedback: 'Feedback',
  updates: 'Updates',
};

export function bucketOf(item: MyWorkItem): MyWorkBucket {
  if (item.item_type === 'routed_record') return 'routed';
  if (item.item_type === 'my_task') return 'tasks';
  if (item.item_type === 'app_feedback') return 'feedback';
  if (item.item_type === 'notification') return 'updates';
  return 'approvals';
}

export const ITEM_TYPE_LABEL: Record<string, string> = {
  routed_record: 'Routed',
  rate_proposal: 'Rate change',
  facility_access_proposal: 'Facility access',
  user_approval: 'User approval',
  am_collect_submission: 'AM Collect',
  deletion_request: 'Deletion',
  incentive_validation: 'Incentive',
  incentive_finance: 'Incentive · finance',
  contract_approval: 'Contract',
  hiring_approval: 'Hiring',
  supply_approval: 'Supply',
  sub_replacement_approval: 'Sub replacement',
  my_task: 'Task',
  app_feedback: 'Feedback',
  open_requirement: 'Missing info',
  notification: 'Update',
  esign_outstanding: 'Awaiting signature',
  // 2026-09-03, 2D. A computed row: the caller's own Draft / Awaiting_AM_Input
  // project records, one line for all of them. Labelled here rather than left
  // to render its raw item_type — see the NOTE below for the three that are.
  project_work_unsent: 'Not sent',
  // NOTE: kicker_seed_review, kicker_due and project_payment_review also feed
  // this list and have no entry here, so they render their raw item_type.
  // Pre-existing, outside this change's scope, flagged rather than fixed.
};

// --- Read path ---------------------------------------------------------------
export interface MyWorkResult {
  items: MyWorkItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useMyWork(): MyWorkResult {
  const { user } = useAuth();
  const [items, setItems] = useState<MyWorkItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const { data, error: e } = await supabase.rpc('get_my_work');
        if (cancelled) return;
        if (e) throw e;
        // (AM Collect branch dropped server-side in Phase 3 — no client filter.)
        setItems((data ?? []) as MyWorkItem[]);
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err, 'Failed to load My Work.'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tick]);

  return { items, isLoading, error, refetch: () => setTick((n) => n + 1) };
}

// Lightweight count for the nav badge: total items where someone is waiting
// on me. Replaces the old routing-only useInboxCount. Self-handles no-auth.
// Phase 4: this IS the in-app bell — a private, RLS-gated Supabase Realtime
// broadcast channel (topic notify:<eid>, authorized by the realtime.messages
// policy) refreshes the count the moment a recipient row is written.
export function useMyWorkCount(): number {
  const { user, employee } = useAuth();
  const myEid = employee?.employee_id ?? null;
  const [count, setCount] = useState(0);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!user) {
      setCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_my_work');
        if (cancelled) return;
        if (error) throw error;
        const rows = (data ?? []) as MyWorkItem[];
        setCount(rows.filter((r) => r.waiting_on_me).length);
      } catch {
        if (!cancelled) setCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, tick]);

  useEffect(() => {
    if (!user || !myEid) return;
    const channel = supabase
      .channel(`notify:${myEid}`, { config: { private: true } })
      .on('broadcast', { event: '*' }, () => setTick((n) => n + 1))
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, myEid]);
  return count;
}

// --- Inline writes — SAME paths as the source modules ------------------------

// Rate proposals: identical UPDATE to admin/rate-proposals.tsx submitDecision.
// RLS decider_act gates who can write; approval fires rcp_apply_on_approve.
export async function decideRateProposal(
  proposalId: string,
  mode: 'approve' | 'reject',
  deciderEid: string,
  notes: string,
): Promise<void> {
  const { error } = await supabase
    .from('rate_change_proposals')
    .update({
      status: mode === 'approve' ? 'Approved' : 'Rejected',
      decided_by_eid: deciderEid,
      decision_notes: notes.trim(),
    })
    .eq('proposal_id', proposalId);
  if (error) throw error;
}

// User approvals: same provision_user RPC as admin/users.tsx (superuser-only).
export async function approvePendingUser(
  profileId: string,
  role: string,
): Promise<void> {
  const { error } = await supabase.rpc('provision_user', {
    p_target_id: profileId,
    p_role: role,
    p_status: 'active',
  });
  if (error) throw error;
}

// Supply requests: same RPCs as supply-management/approval-queue.tsx.
export async function approveSupplyRequest(requestId: string): Promise<void> {
  const { error } = await supabase.rpc('approve_supply_request', {
    p_request_id: requestId,
  });
  if (error) throw error;
}

export async function rejectSupplyRequest(
  requestId: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase.rpc('reject_supply_request', {
    p_request_id: requestId,
    p_reason: reason.trim(),
  });
  if (error) throw error;
}

// My tasks: identical Done payload to tasks/hooks/use-task-data.ts updateStatus.
export async function completeMyTask(
  taskId: string,
  myEid: string | null,
  completionNotes?: string,
): Promise<void> {
  const payload: Record<string, unknown> = {
    status: 'Done',
    updated_date: new Date().toISOString().slice(0, 10),
    completed_by: myEid,
    completed_date: new Date().toISOString().slice(0, 10),
  };
  if (completionNotes !== undefined && completionNotes.trim()) {
    payload.completion_notes = completionNotes.trim();
  }
  const { error } = await supabase
    .from('facility_tasks')
    .update(payload)
    .eq('task_id', taskId);
  if (error) throw error;
}

// Deletion requests: superuser-only review RPCs (migration
// deletion_request_review_rpcs). Approve re-snapshots the target record and
// hard-deletes it via the same tcs.admin_override mechanics as
// admin_force_delete; reject stamps Rejected and leaves the record alone.
export async function approveDeletionRequest(
  requestId: string,
  notes?: string,
): Promise<void> {
  const { error } = await supabase.rpc('approve_deletion_request', {
    p_request_id: Number(requestId),
    p_notes: notes?.trim() || null,
  });
  if (error) throw error;
}

export async function rejectDeletionRequest(
  requestId: string,
  notes: string,
): Promise<void> {
  const { error } = await supabase.rpc('reject_deletion_request', {
    p_request_id: Number(requestId),
    p_notes: notes.trim(),
  });
  if (error) throw error;
}
